/**
 * Recurring services (schedules) for a customer, grouped by property, or for
 * a single property when `propertyId` is given. Shared by the customer and
 * property detail pages; the page loads the schedules.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import * as types from '../api/types';
import { describeRecurrence } from '../utils/recurrence';
import { formatJobDate } from '../utils/jobPresentation';
import { recurringDetailPath, recurringNewPath, recurringStatusClass, recurringStatusLabel } from '../pages/recurringUi';
import { primaryButtonClass, rowActionClass } from './formStyles';
import { Toast } from './Toast';

interface RecurringServicesSectionProps {
  customerId: string;
  /** Property detail page: the schedules are all at this property (no grouping) */
  propertyId?: string;
  /** The customer's properties, for group order and labels (customer page) */
  properties: types.Property[];
  /** null while loading */
  schedules: types.RecurringSchedule[] | null;
  loadError: string | null;
  canManage: boolean;
}

export function RecurringServicesSection({ customerId, propertyId, properties, schedules, loadError, canManage }: RecurringServicesSectionProps) {
  const byProperty = new Map<string, types.RecurringSchedule[]>();
  (schedules ?? []).forEach((schedule) => {
    byProperty.set(schedule.propertyId, [...(byProperty.get(schedule.propertyId) ?? []), schedule]);
  });
  // Properties in page order first, then any schedule whose property is not listed (deleted)
  const groups: { propertyId: string; label: string }[] = properties
    .filter((property) => byProperty.has(property.id))
    .map((property) => ({ propertyId: property.id, label: property.address }));
  byProperty.forEach((list, groupPropertyId) => {
    if (!groups.some((group) => group.propertyId === groupPropertyId)) groups.push({ propertyId: groupPropertyId, label: list[0].property.address });
  });
  const viewAllQuery = propertyId ? `propertyId=${encodeURIComponent(propertyId)}` : `customerId=${encodeURIComponent(customerId)}`;
  const canCreate = canManage && (Boolean(propertyId) || properties.length > 0);

  return (
    <section aria-labelledby="recurring-services-heading" className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 id="recurring-services-heading" className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Recurring services {schedules && schedules.length > 0 && <span className="text-gray-400 font-normal">({schedules.length})</span>}
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          {schedules && schedules.length > 0 && (
            <Link to={`/recurring?${viewAllQuery}`} className={`${rowActionClass} py-2.5 sm:py-0`}>View all</Link>
          )}
          {canCreate && (
            <Link to={recurringNewPath({ customerId, propertyId })} className={`${primaryButtonClass} inline-flex items-center min-h-[44px] sm:min-h-0`}>New recurring service</Link>
          )}
        </div>
      </div>
      {loadError ? (
        <><Toast kind="error">{loadError}</Toast><p className="text-gray-500 text-sm">Recurring services could not be loaded.</p></>
      ) : schedules === null ? (
        <p className="text-gray-500 text-sm">Loading recurring services...</p>
      ) : schedules.length === 0 ? (
        <p className="text-gray-500 text-sm">{propertyId ? 'No recurring services at this property.' : 'No recurring services for this customer.'}</p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.propertyId}>
              {!propertyId && <p className="text-sm font-medium text-gray-700 mb-1 break-words">{group.label}</p>}
              <ul className="divide-y divide-gray-100">
                {(byProperty.get(group.propertyId) ?? []).map((schedule) => (
                  <li key={schedule.id} className="flex flex-wrap items-center gap-3 py-2">
                    <Link to={recurringDetailPath(schedule.id)} className="text-blue-700 hover:underline">
                      {describeRecurrence(schedule.frequency, schedule.interval, schedule.frequencyDetails)}
                    </Link>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${recurringStatusClass(schedule.status)}`}>
                      {recurringStatusLabel(schedule.status)}
                    </span>
                    <span className="text-xs text-gray-500">
                      Last generated {schedule.lastGeneratedDate ? formatJobDate(schedule.lastGeneratedDate) : 'never'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
